import { useState } from 'react';

import _get from 'lodash/get';
import _head from 'lodash/head';
import _isNil from 'lodash/isNil';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { MESSAGE_DISPLAY_FAST_ID } from 'components/core/BusinessInfo/BusinessInfo.constants';
import { getLayoutComponents } from 'components/core/ImagesPage/LayoutComponentList/LayoutComponentList.utils';
import useAsyncEffect from 'components/modules/foundation/hooks/use-async-effect';
import ErrorHandler from 'libs/errors';

import {
  announcementConfigurationToForm,
  announcementFormToConfiguration,
  announcementFormToCustomFormConfiguration,
  createAnnouncementForm,
  getCustomFormComponentConfiguration,
  legacyConfigurationToForm,
} from './AnnouncementForm.utils';
import { selectLayouts, selectWebsiteId } from '../../../../../selectors/website';
import useComponentConfigurations from '../../../component-configuration/hooks/use-component-configurations';
import { selectComponentConfigurationsByFastId } from '../../../component-configuration/selectors/component-configuration';
import { updateOneComponentConfiguration } from '../../../component-configuration/slices/ComponentConfiguration.slice';
import { ANNOUNCEMENT_FAST_ID } from '../../constants';

export function useAnnouncementForm() {
  const websiteId = useSelector(selectWebsiteId);
  const layouts = useSelector(selectLayouts);
  const defaultLayoutId = _get(layouts, '[0].id');
  const dispatch = useDispatch();

  const {
    isLoading: isLoadingComponentConfigurations,
    upsertComponentConfiguration,
    isSubmitting,
  } = useComponentConfigurations(websiteId, defaultLayoutId);

  const [isLoading, setIsLoading] = useState(true);

  const announcementComponentConfigurations = useSelector((state) =>
    selectComponentConfigurationsByFastId(state, ANNOUNCEMENT_FAST_ID),
  );
  const announcementComponentConfiguration = _head(announcementComponentConfigurations) || {};

  const legacyComponentConfiguration = _get(
    getLayoutComponents(layouts, [MESSAGE_DISPLAY_FAST_ID]),
    '[0]',
    {},
  );

  const initialState = announcementConfigurationToForm(announcementComponentConfiguration);
  const [formData, setFormData] = useState(createAnnouncementForm(initialState));

  async function submit(formDataParam) {
    const finalFormData = formDataParam || formData;
    const newReferenceId = uuidv4();
    const announcementConfiguration = announcementFormToConfiguration(
      { ...finalFormData, referenceId: newReferenceId },
      announcementComponentConfiguration,
    );

    const savedAnnouncementComponentConfiguration = await upsertComponentConfiguration(
      announcementConfiguration,
    );
    const { id: announcementComponentConfigurationId } = savedAnnouncementComponentConfiguration;

    const existingCustomFormComponentConfiguration = getCustomFormComponentConfiguration(
      announcementComponentConfiguration,
    );

    const newCustomFormComponentConfiguration = announcementFormToCustomFormConfiguration(
      finalFormData,
      announcementComponentConfigurationId,
      existingCustomFormComponentConfiguration,
    );
    const savedCustomForm = await upsertComponentConfiguration(newCustomFormComponentConfiguration);

    dispatch(
      updateOneComponentConfiguration({
        id: announcementComponentConfigurationId,
        changes: { childConfigurations: [savedCustomForm] },
      }),
    );
  }

  useAsyncEffect(async () => {
    if (isLoadingComponentConfigurations) {
      return;
    }

    try {
      setIsLoading(true);
      const hasLegacyConfiguration = !_isNil(_get(legacyComponentConfiguration, 'id'));
      const hasAnnouncementConfiguration = !_isNil(_get(announcementComponentConfiguration, 'id'));

      let newState;
      if (!hasAnnouncementConfiguration && hasLegacyConfiguration) {
        newState = legacyConfigurationToForm(legacyComponentConfiguration);
      } else {
        newState = announcementConfigurationToForm(announcementComponentConfiguration);
      }

      const newFormData = createAnnouncementForm(newState);

      if (!hasAnnouncementConfiguration) {
        // automatically create new announcement from legacy configuration
        await submit(newFormData);
      }
      setFormData(newFormData);
    } catch (error) {
      ErrorHandler.capture(error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoadingComponentConfigurations, _get(announcementComponentConfiguration, 'id')]);

  function setValue(field, value) {
    setFormData((prevFormData) => ({ ...prevFormData, [field]: value }));
  }

  const finalIsLoading = isLoadingComponentConfigurations || isLoading;

  return {
    componentConfiguration: announcementComponentConfiguration,
    isLoading: finalIsLoading,
    isSubmitting,
    formData,
    setValue,
    submit,
  };
}
